import Error from "@hudson/ui/components/Error";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import Success from "@hudson/ui/components/Success";
import useContent from "@lib/use_content";
import { useState } from "react";
import AddButton from "./AddButton";
import AddEditForm from "./Form";
import Locations from "./Locations";
import useLocationRemoval from "./hooks/useLocationRemoval";
import useLocationUpdate from "./hooks/useLocationUpdate";

interface LayoutProps {
  locations?: be.Location[];
}

interface ShowFormState {
  method: "add" | "edit";
  pudId?: string;
}

export default function Layout({ locations = [] }: LayoutProps) {
  const { data: contentData, loading } = useContent([
    "beProfileLocationsAddButton",
    "beProfileLocationsTitle",
  ]);

  const [showForm, setShowForm] = useState<ShowFormState | null>(null);
  const {
    clearSuccessAndEdit: updateClearMessages,
    updateLocation,
    errorMessage: updateError,
    success: updateSuccess,
    submitting: updateSubmitting,
  } = useLocationUpdate();
  const {
    clearSuccessAndEdit: removeClearMessages,
    removeLocation,
    errorMessage: removeError,
    success: removeSuccess,
    submitting: removeSubmitting,
  } = useLocationRemoval();

  const submitting = updateSubmitting || removeSubmitting;
  const errorMessage = updateError || removeError;
  const success = updateSuccess || removeSuccess;
  const onSuccess = () => setShowForm(null);

  function updateShowForm(showFormValue) {
    updateClearMessages();
    removeClearMessages();
    setShowForm(showFormValue);
  }

  return loading ? (
    <ConcentricLoader className="max-w-2xl px-4 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
  ) : (
    <>
      {success && <Success className="mb-6" message={success} heavyStyle scrollTo />}
      {errorMessage && <Error className="mb-6" message={errorMessage} heavyStyle />}

      {showForm ? (
        <AddEditForm
          method={showForm.method}
          onHide={() => setShowForm(null)}
          onSubmit={(data) => updateLocation(data, onSuccess)}
          location={locations.find((loc) => loc.id === showForm.pudId)}
          pudId={showForm.pudId}
        />
      ) : (
        <>
          <AddButton
            label={contentData?.content.beProfileLocationsAddButton || ""}
            onClick={() => updateShowForm({ method: "add" })}
          />

          {locations.length > 0 && (
            <>
              <div className="mt-8 mb-4 space-y-1">
                <h3 className="text-xl font-bold leading-6 text-gray-900">
                  {contentData?.content.beProfileLocationsTitle}
                </h3>
              </div>

              <Locations
                locations={locations}
                onEdit={(pudId) => updateShowForm({ method: "edit", pudId })}
                onRemove={(data) => removeLocation(data, onSuccess)}
                submitting={submitting}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
